import { useEffect, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { MembershipBadge } from "@/components/MembershipBadge";
import { MediaPreview } from "@/components/MediaUploader";
import { PostComposer } from "@/components/social/PostComposer";
import { PostCard } from "@/components/social/PostCard";
import { VoteButtons } from "@/components/social/VoteButtons";
import { MemberDirectory } from "@/components/social/MemberDirectory";
import { MapPin, MessageCircle, Image as ImageIcon, FileText, Film, Radio, Activity, Users } from "lucide-react";

function Avatar({ url, name, size = 28 }) {
    const initial = (name || "?").charAt(0).toUpperCase();
    if (url) {
        const full = url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
        return <img src={full} alt="" className="rounded-full object-cover flex-shrink-0" style={{ width: size, height: size }} />;
    }
    return (
        <div className="rounded-full bg-[var(--ok-accent)] text-black font-display font-black flex items-center justify-center flex-shrink-0" style={{ width: size, height: size, fontSize: size * 0.42 }}>
            {initial}
        </div>
    );
}

function timeAgo(iso) {
    if (!iso) return "";
    const ms = Date.now() - new Date(iso).getTime();
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s/60)}m ago`;
    if (s < 86400) return `${Math.floor(s/3600)}h ago`;
    if (s < 604800) return `${Math.floor(s/86400)}d ago`;
    return new Date(iso).toLocaleDateString();
}

function TierBadge({ tier }) {
    if (tier === "research_council") return (
        <span className="inline-flex items-center gap-1" title="Research Council">
            <MembershipBadge tier="research_council" size="sm" />
            <span className="font-mono text-[9px] uppercase tracking-widest" style={{ color: "#e7c873" }}>Council</span>
        </span>
    );
    if (tier === "verified_witness") return (
        <span className="inline-flex items-center gap-1" title="Verified Witness">
            <MembershipBadge tier="verified_witness" size="sm" />
            <span className="font-mono text-[9px] uppercase tracking-widest opacity-80">Witness</span>
        </span>
    );
    return null;
}

export default function CommunityFeed() {
    const { user } = useAuth();
    const location = useLocation();
    const [tab, setTab] = useState("wall"); // wall | activity

    return (
        <div className="ok-container py-10 max-w-4xl" data-testid="community-feed-page">
            <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 mb-2">Live Activity · UNVEILED</div>
            <h1 className="font-display text-4xl">Community</h1>
            <p className="opacity-70 mt-2 text-sm leading-relaxed max-w-2xl">
                Your investigation network. Drop sightings and findings to the wall, follow witnesses you trust, and watch
                the live record grow. Verified Witness and Research Council contributions carry their tier insignia.
            </p>

            <div className="mt-6 flex items-center gap-2 flex-wrap" data-testid="community-tabs">
                {[
                    { k: "wall", label: "Main Wall", icon: Radio },
                    { k: "activity", label: "Live Activity", icon: Activity },
                    { k: "members", label: "Members", icon: Users },
                ].map(({ k, label, icon: Icon }) => (
                    <button
                        key={k}
                        onClick={() => setTab(k)}
                        className={`px-3 py-1.5 rounded-sm font-mono uppercase text-[10px] tracking-widest border transition flex items-center gap-1 ${tab === k ? "bg-[var(--ok-accent)] text-black border-[var(--ok-accent)]" : "opacity-70 hover:opacity-100 border-[var(--ok-border)]"}`}
                        data-testid={`community-tab-${k}`}
                    >
                        <Icon className="w-3 h-3" /> {label}
                    </button>
                ))}
            </div>

            {tab === "wall" ? <MainWall key={location.key} /> : tab === "members" ? <MemberDirectory /> : <ActivityFeed />}
        </div>
    );
}

function MainWall() {
    const [posts, setPosts] = useState([]);
    const [personalized, setPersonalized] = useState(false);
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        api.get("/social/wall", { params: { limit: 60 } })
            .then((r) => {
                setPosts(r.data?.items || []);
                setPersonalized(!!r.data?.personalized);
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="mt-6" data-testid="main-wall">
            <PostComposer onPosted={(p) => setPosts((prev) => [p, ...prev])} />
            <div className="mt-3 font-mono text-[10px] uppercase tracking-widest opacity-50" data-testid="wall-mode-note">
                {personalized
                    ? "Personalized · posts from your network first, then trending"
                    : "Follow witnesses to personalize your wall. Showing trending, recent and member activity"}
            </div>
            <div className="mt-4 space-y-3" data-testid="wall-posts">
                {loading ? (
                    <div className="text-xs font-mono opacity-50">Loading the wall…</div>
                ) : posts.length === 0 ? (
                    <div className="ok-card p-6 text-sm opacity-70 text-center" data-testid="wall-empty">
                        Nothing on the wall yet. Be the first to drop a sighting.
                    </div>
                ) : posts.map((p) => (
                    <PostCard key={p.id} post={p} onRemoved={(id) => setPosts((prev) => prev.filter((x) => x.id !== id))} />
                ))}
            </div>
        </div>
    );
}

function ActivityFeed() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all"); // all | encounter | comment | media | post
    const [mediaVotes, setMediaVotes] = useState(null); // { id: {score, my} }

    useEffect(() => {
        api.get("/community/feed", { params: { limit: 60 } })
            .then(async (r) => {
                const feed = r.data?.items || [];
                setItems(feed);
                // batch-fetch vote tallies for member media so uploads are votable
                const mediaIds = feed.filter((i) => i.kind === "media").map((i) => i.id);
                if (mediaIds.length) {
                    const idsCsv = mediaIds.join(",");
                    const votes = {};
                    try {
                        const [cRes, mRes] = await Promise.all([
                            api.get("/social/counts", { params: { target_type: "media", ids: idsCsv } }),
                            api.get("/social/votes/me", { params: { target_type: "media", ids: idsCsv } }).catch(() => ({ data: { votes: {} } })),
                        ]);
                        const counts = cRes.data?.counts || {};
                        const mine = mRes.data?.votes || {};
                        mediaIds.forEach((id) => {
                            votes[id] = { score: counts[id]?.vote_score || 0, my: mine[id] || 0 };
                        });
                    } catch { /* non-fatal */ }
                    setMediaVotes(votes);
                } else {
                    setMediaVotes({});
                }
            })
            .finally(() => setLoading(false));
    }, []);

    const filtered = filter === "all" ? items : items.filter((i) => i.kind === filter);

    return (
        <div className="mt-6" data-testid="activity-feed">
            <div className="flex items-center gap-2 flex-wrap" data-testid="feed-filter">
                {[
                    { k: "all", label: "All" },
                    { k: "post", label: "Drops", icon: Radio },
                    { k: "encounter", label: "Encounters", icon: FileText },
                    { k: "comment", label: "Discussion", icon: MessageCircle },
                    { k: "media", label: "Media", icon: ImageIcon },
                ].map(({ k, label, icon: Icon }) => (
                    <button
                        key={k}
                        onClick={() => setFilter(k)}
                        className={`px-3 py-1.5 rounded-sm font-mono uppercase text-[10px] tracking-widest border transition flex items-center gap-1 ${filter === k ? "bg-[var(--ok-accent)] text-black border-[var(--ok-accent)]" : "opacity-70 hover:opacity-100 border-[var(--ok-border)]"}`}
                        data-testid={`feed-filter-${k}`}
                    >
                        {Icon ? <Icon className="w-3 h-3" /> : null}
                        {label}
                    </button>
                ))}
            </div>

            <div className="mt-6 space-y-3">
                {loading ? (
                    <div className="text-xs font-mono opacity-50">Loading…</div>
                ) : filtered.length === 0 ? (
                    <div className="ok-card p-6 text-sm opacity-70 text-center">Nothing here yet.</div>
                ) : filtered.map((i) => (
                    <FeedItem key={`${i.kind}-${i.id}`} item={i} mediaVotes={mediaVotes} />
                ))}
            </div>
        </div>
    );
}

function FeedItem({ item, mediaVotes }) {
    if (item.kind === "post") {
        return <PostCard post={item} />;
    }

    if (item.kind === "encounter") {
        return (
            <Link to={`/encounters/${item.id}`} className="ok-card p-4 block hover:border-[var(--ok-accent)] transition" data-testid={`feed-encounter-${item.id}`}>
                <div className="flex gap-3">
                    {item.thumbnail_url ? (
                        <img src={item.thumbnail_url.startsWith("http") ? item.thumbnail_url : `${process.env.REACT_APP_BACKEND_URL}${item.thumbnail_url}`}
                             alt="" className="w-20 h-20 object-cover rounded-sm flex-shrink-0" />
                    ) : (
                        <div className="w-20 h-20 bg-[var(--ok-surface-2)] rounded-sm flex items-center justify-center text-2xl opacity-40">?</div>
                    )}
                    <div className="flex-1 min-w-0">
                        <div className="font-mono text-[10px] uppercase tracking-widest opacity-60 flex items-center gap-2">
                            <FileText className="w-3 h-3" /> New encounter · {timeAgo(item.created_at)}
                        </div>
                        <div className="font-display text-lg mt-0.5 truncate">{item.title}</div>
                        {item.location && (
                            <div className="text-xs opacity-70 mt-0.5 flex items-center gap-1"><MapPin className="w-3 h-3" /> {item.location}</div>
                        )}
                        {item.what_happened && (
                            <p className="text-sm mt-1 opacity-80 line-clamp-2">{item.what_happened}</p>
                        )}
                    </div>
                </div>
            </Link>
        );
    }

    if (item.kind === "comment") {
        const targetLink = item.target_type === "encounter"
            ? `/encounters/${item.target_id}`
            : item.target_type === "media" ? `/media-archive/${item.target_id}`
            : item.target_type === "post" ? "/community" : `/species/${item.target_id}`;
        const elevated = item.user_subscription_tier === "research_council";
        return (
            <div className={`ok-card p-4 ${elevated ? "border-l-2" : ""}`}
                 style={elevated ? { borderLeftColor: "#e7c873", background: "rgba(231,200,115,0.04)" } : undefined}
                 data-testid={`feed-comment-${item.id}`}>
                <div className="flex items-start gap-3">
                    <Avatar url={item.user_avatar_url} name={item.user_name} size={32} />
                    <div className="flex-1 min-w-0">
                        <div className="text-xs flex items-center gap-2 flex-wrap">
                            <Link to={`/u/${item.user_id}`} className="font-mono uppercase tracking-wide hover:text-[var(--ok-accent)]">{item.user_name || "Witness"}</Link>
                            <TierBadge tier={item.user_subscription_tier} />
                            <span className="opacity-40">·</span>
                            <Link to={targetLink} className="font-mono opacity-70 hover:text-[var(--ok-accent)]">commented</Link>
                            <span className="opacity-50">· {timeAgo(item.created_at)}</span>
                        </div>
                        <p className="text-sm mt-1 line-clamp-3 whitespace-pre-wrap break-words">{item.body}</p>
                        {item.media && <div className="mt-2"><MediaPreview media={item.media} /></div>}
                    </div>
                </div>
            </div>
        );
    }

    if (item.kind === "media") {
        const v = mediaVotes?.[item.id];
        return (
            <div className="ok-card p-4" data-testid={`feed-media-${item.id}`}>
                <div className="flex items-start gap-3">
                    <Avatar url={item.user_avatar_url} name={item.user_name} size={32} />
                    <div className="flex-1 min-w-0">
                        <div className="text-xs flex items-center gap-2 flex-wrap">
                            <Link to={`/u/${item.user_id}`} className="font-mono uppercase tracking-wide hover:text-[var(--ok-accent)]">{item.user_name || "Witness"}</Link>
                            <TierBadge tier={item.user_subscription_tier} />
                            <span className="opacity-40">·</span>
                            <span className="font-mono opacity-70 flex items-center gap-1">
                                {item.media_type === "video" ? <Film className="w-3 h-3" /> : <ImageIcon className="w-3 h-3" />}
                                uploaded {item.media_type}
                            </span>
                            <span className="opacity-50">· {timeAgo(item.created_at)}</span>
                        </div>
                        <div className="mt-2">
                            <MediaPreview media={item} />
                        </div>
                        {v && (
                            <div className="mt-2" data-testid={`feed-media-votes-${item.id}`}>
                                <VoteButtons
                                    targetType="media"
                                    targetId={item.id}
                                    initialScore={v.score}
                                    initialMyVote={v.my}
                                    layout="horizontal"
                                    size="sm"
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
    return null;
}
